import React, { useEffect, useState } from 'react';
import { authAPI, subscriptionAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Mail, CreditCard, Loader2, CheckCircle2 } from 'lucide-react';

/**
 * Verification banner for free-tier rendering.
 *
 *   Step 1. Verify email (only gate left after v1.5 simplification)
 *
 * The banner self-fetches user status on mount; the parent doesn't have
 * to thread it through. Hides itself once the email is verified. Shown
 * above the Dashboard's main content so it's the first thing a user
 * sees on a fresh signup.
 *
 * v1.5 dropped the card-on-file gate entirely (it added meaningful
 * friction without commensurate anti-abuse gain. Email verification +
 * the 5/month free cap are sufficient). The card-setup CTA below is
 * left in place but only shown when explicitly requested via
 * `?show_card=1` on the URL. Useful for power users who want to
 * pre-load a payment method, but no longer mandatory. Most users
 * will never see it.
 */
export default function AccountVerificationBanner() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [resending, setResending] = useState(false);
  const [openingSetup, setOpeningSetup] = useState(false);

  const refresh = async () => {
    try {
      const res = await authAPI.getMe();
      setUser(res.data);
    } catch (_) {
      // Silent: 401 redirects via the global axios interceptor.
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // Hide the banner entirely when the email is verified (or while we're
  // still fetching). v1.5 removed the card-on-file gate entirely so
  // there's only one thing to nag about now.
  if (!user) return null;
  const needsEmailVerify = !user.email_verified;
  // The legacy `needsCard` check stays as a no-op variable so the
  // commented-out CTA branch below still type-checks if someone
  // re-enables it. Effectively `false` now.
  const needsCard = false;
  if (!needsEmailVerify) return null;

  const handleResend = async () => {
    setResending(true);
    try {
      await authAPI.resendVerification();
      toast.success(
        t('verify.resendSent') ||
          'Wir haben dir eine neue Bestätigungs-E-Mail geschickt. Schau in dein Postfach (auch Spam).'
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      // 429 from rate limit
      if (err.response?.status === 429) {
        toast.error(
          t('verify.resendThrottled') ||
            'Zu viele Anfragen. Bitte warte ein paar Minuten und versuch es erneut.'
        );
      } else {
        toast.error(detail || t('verify.resendFailed') || 'Konnte die E-Mail nicht senden.');
      }
    } finally {
      setResending(false);
    }
  };

  const handleOpenCardSetup = async () => {
    setOpeningSetup(true);
    try {
      const res = await subscriptionAPI.createSetupIntent();
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error('no setup url');
      }
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          t('verify.cardSetupFailed') ||
          'Konnte die Karten-Verifizierung nicht öffnen.'
      );
      setOpeningSetup(false);
    }
  };

  // We render ONE banner per outstanding step. If both are needed, show
  // the email one first (it's the prerequisite for the card step on
  // first signup, and tackling them stacked in order avoids a wall of
  // banners at the top of the dashboard).
  if (needsEmailVerify) {
    return (
      <div
        className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-xl border border-[#c7a869] bg-[#f6edd8]"
        data-testid="verify-email-banner"
      >
        <div className="flex items-start gap-3 min-w-0">
          <Mail className="w-5 h-5 text-[#9a6d1f] mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-[#9a6d1f]">
              {t('verify.emailTitle') || 'Bitte bestätige deine E-Mail-Adresse'}
            </p>
            <p className="text-xs text-[#7a5619] mt-1">
              {t('verify.emailHelp') ||
                'Wir haben dir einen Bestätigungs-Link geschickt. Bis du klickst, kannst du keine Videos erstellen.'}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleResend}
          disabled={resending}
          className="border-[#9a6d1f]/50 text-[#9a6d1f] hover:bg-[#f0e2c2] w-full sm:w-auto sm:shrink-0"
        >
          {resending ? (
            <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> {t('common.loading') || 'Wird gesendet…'}</>
          ) : (
            <>{t('verify.resend') || 'Bestätigung erneut senden'}</>
          )}
        </Button>
      </div>
    );
  }

  // needsCard branch
  return (
    <div
      className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-xl border border-[#1f4d3a]/30 bg-[#e9efe7]"
      data-testid="add-card-banner"
    >
      <div className="flex items-start gap-3 min-w-0">
        <CreditCard className="w-5 h-5 text-[#1f4d3a] mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-[#2e6b50]">
            {t('verify.cardTitle') || 'Karte hinterlegen. Kostenlos, keine Belastung'}
          </p>
          <p className="text-xs text-[#23211a]/80 mt-1">
            {t('verify.cardHelp') ||
              'Wir prüfen nur kurz, ob die Karte echt ist (€0 Autorisierung). Es wird nichts abgebucht. Erst danach können wir dein erstes Video erstellen. So verhindern wir Missbrauch.'}
          </p>
        </div>
      </div>
      <Button
        size="sm"
        onClick={handleOpenCardSetup}
        disabled={openingSetup}
        className="bg-[#1f4d3a] hover:bg-[#1f4d3a] text-white w-full sm:w-auto sm:shrink-0"
      >
        {openingSetup ? (
          <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> {t('common.loading') || 'Wird geöffnet…'}</>
        ) : (
          <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> {t('verify.cardCta') || 'Karte hinterlegen'}</>
        )}
      </Button>
    </div>
  );
}
